import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'

import { GeckoComponentSpace } from './geckoComponentSpace'

/**
 * A class representing a point on a component's surface. Note that the x and
 * y measurement might be in feet, meters, tubes, degrees, or some other
 * measurement unit.
 */
export class GeckoComponentCoords {
  //----------------------------------------------------------------------------

  /** The Rolodex ID of the component these coords are on */
  public readonly componentID: string

  /** The x component of this point */
  public readonly x: GeckoMeasurement

  /** The y component of this point */
  public readonly y: GeckoMeasurement

  /** The units of the 2-dimensional space that these coords exist in */
  public readonly space: GeckoComponentSpace

  /**
   * A simple constructor- private to encourage use of more descriptive static
   * factory methods like CreateFromValues()
   * @param componentID   The Rolodex ID of the component these coords are on
   * @param x             The x component of this point
   * @param y             The y component of this point
   * @private
   */
  private constructor(
    componentID: string,
    x: GeckoMeasurement,
    y: GeckoMeasurement
  ) {
    this.componentID = componentID
    this.x = x
    this.y = y
    this.space = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      x.unit,
      y.unit
    )
  }

  /**
   * A factory method creating a GeckoComponentCoords object from raw data
   * @param componentID   The Rolodex ID of the component these coords are on
   * @param x             The x coordinate
   * @param xUnit         The measurement unit of the x coordinate
   * @param y             The y coordinate
   * @param yUnit         The measurement unit of the y coordinate
   * @constructor
   */
  public static CreateFromValues(
    componentID: string,
    x: number,
    xUnit: GeckoMeasurementUnit,
    y: number,
    yUnit: GeckoMeasurementUnit
  ): GeckoComponentCoords {
    return GeckoComponentCoords.CreateFromMeasurements(
      componentID,
      GeckoMeasurement.CreateFromValue(x, xUnit),
      GeckoMeasurement.CreateFromValue(y, yUnit)
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A factory method creating a GeckoComponentCoords object two GeckoValues
   * @param componentID     The Rolodex ID of the component these coords are on
   * @param x               The x coordinate (including its measurement unit)
   * @param y               The y coordinate (including its measurement unit)
   * @constructor
   */
  public static CreateFromMeasurements(
    componentID: string,
    x: GeckoMeasurement,
    y: GeckoMeasurement
  ): GeckoComponentCoords {
    return new GeckoComponentCoords(componentID, x, y)
  }

  //----------------------------------------------------------------------------

  /**
   * A method for checking whether two component coords are exactly equal,
   * not just equivalent (i.e. (0 in, 0 in) !== (0 ft, 0 ft))
   * @param a               The first GeckoComponentCoords object, or undefined
   * @param b               The second GeckoComponentCoords object, or undefined
   * @constructor
   */
  public static Equals(
    a: GeckoComponentCoords | undefined,
    b: GeckoComponentCoords | undefined
  ): boolean {
    if (a === b) return true
    if (!a) return !b
    if (!b) return !a
    return (
      GeckoMeasurement.Equals(a.x, b.x) && GeckoMeasurement.Equals(a.y, b.y)
    )
  }

  //---------------------------------------------------------------------------

  /**
   * Calculates the Euclidean distance between two points in 2D space.
   * @returns The distance between the two points.  Because the distance unit is
   *          {yUnit} / {xUnit}, it returns the Euclidean distance in the form of
   *          an object with both units listed.
   */
  public static DistanceBetween(
    a: GeckoComponentCoords,
    b: GeckoComponentCoords
  ): {
    num: number
    yUnit: GeckoMeasurementUnit
    xUnit: GeckoMeasurementUnit
  } {
    const bToUse = b.project(a.space)

    if (a.x.unit !== bToUse.x.unit || a.y.unit !== bToUse.y.unit) {
      throw new Error(
        `trying to calculate distance between ComponentCoords with non-convertable units`
      )
    }

    const distance = Math.sqrt(
      Math.pow(a.x.num - bToUse.x.num, 2) + Math.pow(a.y.num - bToUse.y.num, 2)
    )

    return {
      num: distance,
      yUnit: a.y.unit,
      xUnit: a.x.unit,
    }
  }

  //----------------------------------------------------------------------------
  /**
   * A method which returns a GeckoComponentCoords object with new measurement
   * units. Note that if no conversion is necessary, this instance will be
   * returned.
   * @param xUnit           The new measurement unit of the x coordinate
   * @param yUnit           The new measurement unit of the y coordinate
   */
  public convertTo(
    xUnit: GeckoMeasurementUnit | undefined,
    yUnit: GeckoMeasurementUnit | undefined
  ): GeckoComponentCoords {
    if ((this.x.unit === xUnit || !xUnit) && (this.y.unit === yUnit || !yUnit))
      return this
    return GeckoComponentCoords.CreateFromMeasurements(
      this.componentID,
      this.x.convertTo(xUnit),
      this.y.convertTo(yUnit)
    )
  }

  /**
   * A method which returns a GeckoComponentCoords object with new measurement
   * units, so long as the component IDs match.
   * @param space           The new 2-dimensional measurement space to convert to
   */
  public project(space: GeckoComponentSpace): GeckoComponentCoords {
    if (this.componentID !== space.componentID)
      throw new Error(
        `trying to convert ComponentCoords in a space on ${this.componentID} 
        to a space on ${space.componentID}`
      )
    if (this.x.unit === space.x.unit && this.y.unit === space.y.unit)
      return this
    return GeckoComponentCoords.CreateFromMeasurements(
      this.componentID,
      this.x.convertTo(space.x.unit),
      this.y.convertTo(space.y.unit)
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A method returning a legible string form of this object's data
   */
  public toString(): string {
    return `${this.componentID} (${this.x}, ${this.y})`
  }
}
